/**
 * Copyright (c) 2021-2024 Kognitos, Inc. All rights reserved.
 */
import AppConstants from '@/utils/AppConstants';
import AppUtil from '@/utils/AppUtil';
import reactStringReplace from 'react-string-replace';
import { Text } from 'slate';
import FuzzySet from 'fuzzyset';
import Typo from 'typo-js';
import {
  DocumentLineInput,
  EditorLeafDecoration
} from '../Editor/editorInterface';

// TS ignore is needed as the generated type has a bug
// @ts-ignore
const dictionary = new Typo('en_US', false, false, {
  dictionaryPath: '/typo/dictionaries'
});

export enum WIDGET_TYPES {
  FILE = 'file',
  URL = 'url',
  JSON = 'json',
  MARKDOWN = 'markdown',
  // DATE = 'date',
  EMAIL_HTML = 'email_html',
  EMAIL_SUBJECT = 'email_subject',
  EMAIL_BODY = 'email_body'
  // SEND_EMAIL = 'send_email',
  // ASK_KONCIERGE = 'ask_koncierge'
}

export interface IWidgetData {
  lineId: string;
  widgetType: WIDGET_TYPES;
  widgetKey: string;
  widgetValue: string;
}

const editableWidgetTypes = [
  // WIDGET_TYPES.FILE,
  WIDGET_TYPES.MARKDOWN,
  // WIDGET_TYPES.JSON,
  WIDGET_TYPES.URL
  // WIDGET_TYPES.DATE
  // WIDGET_TYPES.SEND_EMAIL,
  // WIDGET_TYPES.ASK_KONCIERGE
];

export const editableWidgets = editableWidgetTypes.map((i) => ({
  type: i,
  label: i.trim().toLowerCase().split('_').join(' ')
}));

export const WIDGET_COUNT_INIT_STATE = {
  KOG_WIDGET_COUNT: {
    [WIDGET_TYPES.MARKDOWN]: 0,
    [WIDGET_TYPES.JSON]: 0,
    [WIDGET_TYPES.URL]: 0
  }
};

function findAllOccurrencesOfSubstr(str: string, sub: string) {
  const occurrences: { index: number; substring: string }[] = [];

  if (sub.length === 0) return occurrences;

  for (let i = 0; i < str.length; i += 1) {
    if (str.substring(i, i + sub.length) === sub) {
      occurrences.push({ index: i, substring: sub });
      i += sub.length - 1; // Skip the substring length to avoid overlapping matches
    }
  }

  return occurrences;
}

const findWidgetsInText = (text: string, keyArr: string[]) => {
  if (keyArr.length === 0) {
    return [];
  }
  const matches = keyArr.filter((key) => text.includes(key));

  if (matches) {
    const widgetsConfig: (string | number)[][] = [];
    matches.forEach((match) => {
      findAllOccurrencesOfSubstr(text, match.trim()).forEach((oc) => {
        widgetsConfig.push([oc.substring.trim(), oc.index, match]);
      });
    });
    return widgetsConfig;
  }
  return [];
};

export const widgetDecorator = (
  [node, path]: any,
  replacerObj: any | undefined
) => {
  const nodeText = node.text;

  if (!nodeText || !replacerObj) return [];

  const urls = findWidgetsInText(nodeText, Object.keys(replacerObj));

  return urls.map(([url, index, widgetKey]: any) => {
    return {
      anchor: {
        path,
        offset: index
      },
      focus: {
        path,
        offset: index + url.length
      },
      decoration: EditorLeafDecoration.Widget,
      widgetKey,
      widgetData: replacerObj[widgetKey]
    };
  });
};

/**
 * {
 * [md1]: {val: 'asdasd', type: 'md'},
 * [md2]: {val: 'asdasd', type: 'md'},,
 * KOG_WIDGET_COUNT: {
 *  md: 2, json: 3
 * }
 * }
 */

export const textFormatter = (text: string, oldObj?: any) => {
  let replacerObj = { ...WIDGET_COUNT_INIT_STATE, ...oldObj };

  const inlineEmailHtml = reactStringReplace(
    text,
    AppConstants.PATTERNS.EMAIL_HTML,
    (match) => {
      replacerObj = {
        ...replacerObj,
        'email-html': { value: `"${match}"`, type: WIDGET_TYPES.EMAIL_HTML }
      };
      return 'the email html is email-html';
    }
  );

  const inlineEmailBody = reactStringReplace(
    inlineEmailHtml,
    AppConstants.PATTERNS.EMAIL_BODY,
    (match) => {
      const key = 'email-body';
      replacerObj = {
        ...replacerObj,
        [key]: { value: `"${match}"`, type: WIDGET_TYPES.EMAIL_BODY }
      };
      return 'the email body is email-body';
    }
  );

  const inlineMD = reactStringReplace(
    inlineEmailBody,
    AppConstants.PATTERNS.MARKDOWN_TEXT,
    (match) => {
      const widgetNumber =
        replacerObj.KOG_WIDGET_COUNT[WIDGET_TYPES.MARKDOWN] + 1;
      const widgetKey = `[md${widgetNumber}]`;
      const newWidgetCount = {
        ...replacerObj.KOG_WIDGET_COUNT,
        [WIDGET_TYPES.MARKDOWN]: widgetNumber
      };
      replacerObj = {
        ...replacerObj,
        [widgetKey]: { value: `"""${match}"""`, type: WIDGET_TYPES.MARKDOWN },
        KOG_WIDGET_COUNT: newWidgetCount
      };
      return widgetKey;
    }
  );

  const inlineFiles = reactStringReplace(
    inlineMD,
    AppConstants.PATTERNS.S3_URI_WIDGET,
    (match) => {
      const uri = `s3://${match}`;
      const s3Parts = AppUtil.getS3URIParts(uri);
      if (s3Parts && s3Parts.filename) {
        replacerObj = {
          ...replacerObj,
          [s3Parts?.filename]: { value: `"${uri}"`, type: WIDGET_TYPES.FILE }
        };
      }

      return s3Parts?.filename ? `${s3Parts?.filename}` : uri;
    }
  );

  const inlineURL = reactStringReplace(
    inlineFiles,
    AppConstants.PATTERNS.HTTP_URI,
    (match) => {
      const widgetNumber = replacerObj.KOG_WIDGET_COUNT[WIDGET_TYPES.URL] + 1;
      const widgetKey = `[url${widgetNumber}]`;
      const newWidgetCount = {
        ...replacerObj.KOG_WIDGET_COUNT,
        [WIDGET_TYPES.URL]: widgetNumber
      };
      replacerObj = {
        ...replacerObj,
        [widgetKey]: { value: `"${match}"`, type: WIDGET_TYPES.URL },
        KOG_WIDGET_COUNT: newWidgetCount
      };
      return widgetKey;
    }
  );

  const inlineJSON = reactStringReplace(
    inlineURL,
    AppConstants.PATTERNS.JSON_WIDGET,
    (match) => {
      // For some reason the string needed to be parsed twice, but its not a problem as on error, the fn returns the og value
      const parsedJSON = AppUtil.safeParseJSON(
        AppUtil.safeParseJSON(match, true),
        true
      );
      const isValidJson =
        match &&
        typeof parsedJSON === 'object' &&
        Object.keys(parsedJSON).length > 0;
      if (isValidJson) {
        const widgetNumber =
          replacerObj.KOG_WIDGET_COUNT[WIDGET_TYPES.JSON] + 1;
        const widgetKey = `[json${widgetNumber}]`;
        const newWidgetCount = {
          ...replacerObj.KOG_WIDGET_COUNT,
          [WIDGET_TYPES.JSON]: widgetNumber
        };
        replacerObj = {
          ...replacerObj,
          [widgetKey]: {
            value: match,
            type: WIDGET_TYPES.JSON
          },
          KOG_WIDGET_COUNT: newWidgetCount
        };
        return widgetKey;
      }
      return match;
    }
  );

  return { text: inlineJSON.join(''), replacerObj };
};

const DeFormatPriority = [
  WIDGET_TYPES.FILE,
  WIDGET_TYPES.JSON,
  WIDGET_TYPES.MARKDOWN,
  WIDGET_TYPES.URL,
  WIDGET_TYPES.EMAIL_SUBJECT,
  WIDGET_TYPES.EMAIL_BODY,
  WIDGET_TYPES.EMAIL_HTML
];

export const textDeFormatterForDocumentLine = (
  document: DocumentLineInput[],
  replacerObj?: Record<string, any>
) => {
  // If replacerObj is not provided, return the original document
  if (!replacerObj) {
    return document;
  }

  // Initialize a map to associate each type with its corresponding keys
  const typeToKeysMap = new Map();
  // Populate the map with each type in DeFormatPriority
  for (const type of DeFormatPriority) {
    typeToKeysMap.set(type, []);
  }

  // Loop through keys in replacerObj
  for (const key of Object.keys(replacerObj)) {
    // Retrieve the type of the current key
    const type = replacerObj[key].type;
    // If the type is in the map, add the key to the corresponding array
    if (typeToKeysMap.has(type)) {
      typeToKeysMap.get(type).push(key);
    }
  }

  // Iterate over the document, modifying each line as needed
  return document.map((line) => {
    // Skip modification if the line doesn't contain text
    if (!line.text) {
      return line;
    }

    // Start with the original text of the line
    let newtext = line.text;
    // Iterate over each type in DeFormatPriority
    for (const type of DeFormatPriority) {
      // Get the keys associated with the current type
      const keys = typeToKeysMap.get(type);
      // Iterate over these keys
      for (const key of keys) {
        // If the current key is found in the text, replace it with its value
        if (newtext.includes(key)) {
          newtext = newtext.replaceAll(key, replacerObj[key].value as string);
        }
      }
    }

    // Return the modified line with updated text
    return { ...line, text: newtext };
  });
};

export const textDeFormatter = (
  text: string,
  replacerObj?: Record<string, any>
) => {
  if (!replacerObj) {
    return text;
  }
  const organizedKeys = DeFormatPriority.reduce((acc, type) => {
    Object.keys(replacerObj).forEach((key) => {
      if (replacerObj[key].type === type) {
        acc.push(key);
      }
    });
    return acc;
  }, [] as string[]);

  // Apply replacements in the order of priority
  let newText = text;
  organizedKeys.forEach((key) => {
    newText = newText.replaceAll(key, replacerObj[key].value as string);
  });

  return newText;
};

export const editWidgetEvent = (key: string) => {
  const timeout = setTimeout(() => {
    const event = new CustomEvent('EditWidgetEvent', {
      detail: key
    });
    document.dispatchEvent(event);
    clearTimeout(timeout);
  }, 1);
};
const generateNgrams = (text: string, n: number) => {
  const words = text.split(/\s+/);
  const ngrams = [];
  for (let i = 0; i <= words.length - n; i += 1) {
    ngrams.push(words.slice(i, i + n).join(' '));
  }
  return ngrams;
};

const removeSpecialChars = (text: string) => {
  const startsWith = ['"', '{'];
  const endsWith = [
    '.',
    ',',
    '!',
    '?',
    ';',
    ':',
    '"',
    "'s", // Apostrophe s
    '}',
    '"'
  ];

  let newText = text;
  startsWith.forEach((char) => {
    if (newText.startsWith(char)) {
      newText = newText.replace(char, '');
    }
  });

  endsWith.forEach((char) => {
    if (newText.endsWith(char)) {
      newText = newText.replace(char, '');
    }
  });

  return newText;
};

export const highlightDecorator = (
  [node, path]: any,
  search: string | undefined
) => {
  const ranges: any = [];
  const { text } = node;

  if (!search) return ranges;

  if (search && Text.isText(node)) {
    const searchTermLength = search.split(/\s+/).length; // Number of words in search term
    const ngrams = generateNgrams(text, searchTermLength); // Generate NGrams
    const fuzzyMatchSet = FuzzySet([search], false); // Create fuzzy match set

    const threshold = searchTermLength === 1 ? 0.4 : 0.6; // Set threshold based on number of words in search term

    ngrams.forEach((ngram) => {
      const fuzzyResults = fuzzyMatchSet.get(ngram); // Get fuzzy match results
      if (fuzzyResults) {
        const [bestMatchScore] = fuzzyResults[0]; // Get best match score
        const start = text.indexOf(ngram); // Get start index of ngram
        const end = start + ngram.length; // Get end index of ngram
        if (ngram.toLowerCase() === search) {
          // Exact match, highlight normally
          ranges.push({
            anchor: { path, offset: start },
            focus: { path, offset: end },
            decoration: EditorLeafDecoration.Highlight
          });
        } else if (bestMatchScore > threshold) {
          // Similar but not exact, check spelling
          const words = ngram.split(/\s+/);
          let currentOffset = start;

          ranges.push({
            anchor: { path, offset: start },
            focus: { path, offset: end },
            decoration: EditorLeafDecoration.Highlight
          });

          // Check spelling of each word
          words.forEach((word, index) => {
            const wordStart = currentOffset; // Get start index of word
            const tempWord = removeSpecialChars(word); // Remove special characters
            // Change currentOffset if the word has special characters
            if (tempWord !== word) {
              currentOffset += word.indexOf(tempWord); // Add index of special character
            }
            const wordEnd = currentOffset + tempWord.length; // Get end index of word

            // Check spelling using typo js dictionary
            if (!dictionary.check(tempWord)) {
              // Misspelled word
              ranges.push({
                anchor: { path, offset: wordStart },
                focus: { path, offset: wordEnd },
                decoration: EditorLeafDecoration.Error // Mark as spelling error
              });
            }
            currentOffset += word.length + (index < words.length - 1 ? 1 : 0); // add 1 for space if not the last word
          });
        }
      }
    });
  }

  /** Don't remove may be needed for further optimization */
  // if (search && Text.isText(node)) {
  //   const { text } = node;
  //   const parts = text.split(search);
  //   let offset = 0;

  //   parts.forEach((part, i) => {
  //     if (i !== 0) {
  //       ranges.push({
  //         anchor: { path, offset: offset - search.length },
  //         focus: { path, offset },
  //         decoration: 'highlight'
  //       });
  //     }

  //     offset = offset + part.length + search.length;
  //   });
  // }

  return ranges;
};

const decorators = {
  widget: widgetDecorator,
  highlight: highlightDecorator
};

// Combined decorate function
export const decorate = (
  [node, path]: any,
  search: string | undefined,
  replacerObj: any | undefined
) => {
  const ranges = [];

  for (const [key, value] of Object.entries(decorators)) {
    switch (key) {
      case EditorLeafDecoration.Widget:
        ranges.push(...value([node, path], replacerObj));
        break;
      case EditorLeafDecoration.Highlight:
        ranges.push(...value([node, path], search));
        break;
      default:
        break;
    }
  }
  return ranges;
};
